import { useMemo, useState } from 'react';

import Tippy from '@tippyjs/react';
import { useRecoilValue } from 'recoil';

import { iaRa } from 'lib/helpers/utility';
import { DEVICE_SIZES, useWindowSize } from 'lib/hooks/useWindowSize';

import { userState } from 'components/state';
import Button from 'components/shared/Button/Button';
import AvatarGroup from 'components/shared/AvatarGroup/AvatarGroup';
import STEP_DEFINITIONS from 'components/shared/OnboardingWizard/lib/stepDefinitions';
import SUBSTEP_DEFINITIONS from 'components/shared/OnboardingWizard/lib/substepDefinitions';
import { isDelegatableTarget } from 'components/shared/OnboardingWizard/lib/delegationRules';

import { COMPLETED_STATUSES } from '../../lib/constants';
import TaskDelegationModal from '../TaskDelegationModal/TaskDelegationModal';

import GoLiveDateBanner from './GoLiveDateBanner';

const Footer = ({
  delegations,
  activeSubstep,
  footerConfig = {},
  canGoPrev = true,
  orchestratorData,
  wizardState = {},
  isAdmin,
  navigateToSubstep = () => {},
  practice
}) => {
  const { device } = useWindowSize();
  const userStateValues = useRecoilValue(userState);
  const [openDelegateModal, setOpenDelegateModal] = useState(false);
  const activeSubstepId = activeSubstep?.substep_id;
  const activeStepId = orchestratorData?.stepId;

  // When the active substep is the only child of its step, delegate at the step level instead.
  const activeStep = wizardState?.steps?.find((step) => step.step_id === activeStepId);
  const isOnlySubstepOfStep = activeStep?.substeps?.length === 1;
  const delegateStepIds = isOnlySubstepOfStep && activeStepId ? [activeStepId] : [];
  const delegateSubstepIds = !isOnlySubstepOfStep && activeSubstepId ? [activeSubstepId] : [];

  const assignments = useMemo(() => {
    const substepAssignments =
      delegations?.substeps?.find((d) => d.substep_id == activeSubstepId)?.assignments || [];

    const stepAssignments =
      delegations?.steps?.find((d) => d.step_id == activeStepId)?.assignments || [];

    return [
      ...stepAssignments,
      ...substepAssignments.filter(
        (subAssignment) =>
          !stepAssignments.some(
            (stepAssignment) => stepAssignment.user_id === subAssignment.user_id
          )
      )
    ];
  }, [delegations, activeStepId, activeSubstepId]);

  const canDelegateHere = isDelegatableTarget({
    stepCode: orchestratorData?.stepCode,
    substepCode: orchestratorData?.substepCode
  });

  const isTermsAndConditionsSubstep =
    orchestratorData?.substepCode ===
    SUBSTEP_DEFINITIONS[STEP_DEFINITIONS.ACKNOWLEDGEMENTS.code].TERMS_AND_CONDITIONS.code;
  const isGoLiveDateSubstepCompleted = COMPLETED_STATUSES.includes(
    iaRa(wizardState?.steps)
      .flatMap((step) => iaRa(step?.substeps))
      .find(
        (substep) =>
          substep?.code ===
          SUBSTEP_DEFINITIONS[STEP_DEFINITIONS.GO_LIVE_DATES_AND_TRAININGS.code]
            .GO_LIVE_DATES_AND_TRAININGS.code
      )?.status
  );
  const currentUserId = userStateValues?.user_id ?? userStateValues?.id;

  const delegationStep = delegations?.steps?.find(
    (s) => s?.step_code === STEP_DEFINITIONS.DELEGATION.code
  );

  const delegationStepAssignments = delegationStep?.assignments || [];

  const isUserAssignedToDelegationStep = delegationStepAssignments.some(
    (a) => a.user_id === currentUserId
  );

  const showDelegationButton =
    delegations != null &&
    (currentUserId === wizardState?.instance?.assigned_to ||
      isAdmin ||
      isUserAssignedToDelegationStep) &&
    canDelegateHere;
  return (
    <>
      <div className="onboarding-footer relative z-[99] flex w-full items-center justify-between gap-4 border-x-0 border-b-0 border-t border-solid border-t-neutral-200 px-6 py-4 ring-black">
        {isTermsAndConditionsSubstep && !isGoLiveDateSubstepCompleted && (
          <GoLiveDateBanner navigateToSubstep={navigateToSubstep} />
        )}
        {showDelegationButton ? (
          assignments.length === 0 ? (
            <Button
              color="white"
              textClassName="text-neutral-700 text-sm"
              text="Delegate this task"
              size={device === DEVICE_SIZES.MOBILE ? 'small' : 'medium'}
              iconRight="delegate-task"
              iconColor="neutral"
              onClick={() => setOpenDelegateModal(true)}
            />
          ) : (
            <div className="flex items-center gap-2">
              <label className="text-xs font-500 text-neutral-600">Task is delegated to</label>
              <div className="flex items-center rounded-3xl border border-solid border-neutral-300 p-0.5">
                <AvatarGroup
                  isBorder={false}
                  className="cursor-pointer shadow-none"
                  itemsToShow={3}
                  tooltipSinglePlacement="top"
                  items={assignments.map((a) => ({
                    id: a?.user_id,
                    fullName: `${a?.first_name || ''} ${a?.last_name || ''}`.trim()
                  }))}
                />
                <Tippy
                  content="Add or remove delegations"
                  className="tippy-dark"
                  placement="top"
                  arrow>
                  <div>
                    <Button
                      size={device === DEVICE_SIZES.MOBILE ? 'small' : 'medium'}
                      color="white"
                      className="h-fit px-1"
                      textClassName="text-neutral-500 text-sm"
                      iconRight="delegate-task"
                      iconColor="neutral"
                      onClick={() => setOpenDelegateModal(true)}
                    />
                  </div>
                </Tippy>
              </div>
            </div>
          )
        ) : assignments.length > 0 && canDelegateHere ? (
          <div className="flex items-center gap-2">
            <label className="text-xs font-500 text-neutral-600">Task is delegated to</label>
            <div className="flex items-center rounded-3xl border border-solid border-neutral-300 p-0.5">
              <AvatarGroup
                isBorder={false}
                className="shadow-none"
                itemsToShow={3}
                items={assignments.map((a) => ({
                  id: a?.user_id,
                  fullName: `${a?.first_name || ''} ${a?.last_name || ''}`.trim()
                }))}
              />
            </div>
          </div>
        ) : (
          <div />
        )}
        <div className="flex items-center gap-4">
          {canGoPrev && footerConfig?.goBackHandler && (
            <Button
              variant="tertiary"
              color="neutral"
              text="Back"
              size={device === DEVICE_SIZES.MOBILE ? 'small' : 'medium'}
              isDisabled={footerConfig?.goBackDisabled}
              onClick={footerConfig?.goBackHandler}
            />
          )}
          {(footerConfig.actions ?? []).map((action) => (
            <Button
              key={action.key || action.id || action.text}
              {...action}
              size={device === DEVICE_SIZES.MOBILE ? 'small' : 'medium'}
              color={action?.color || 'primary'}
            />
          ))}
          {footerConfig?.customRightContent}
        </div>
      </div>
      <TaskDelegationModal
        instanceId={orchestratorData?.instanceId}
        step_ids={delegateStepIds}
        substep_ids={delegateSubstepIds}
        isOpen={openDelegateModal}
        handleClose={() => setOpenDelegateModal(false)}
        assignments={assignments}
        practice={practice}
      />
    </>
  );
};

export default Footer;
